import {storefrontMediaPresentationBuildEnabled} from "./storefrontMediaPresentationFeature";

export function canonicalPublicArtworkUrl(value) {
  const input = String(value || "").trim();
  if (!input) return "";
  // A catalog record's exact Storage download URL is the authoritative identity
  // for its artwork. In particular, private Firebase Storage image objects need
  // their server-issued download token. Stripping query parameters makes a valid
  // per-track source unreadable and causes callers to fall back to a logo or an
  // unrelated image. This function deliberately preserves the full URL.
  return input;
}

export const MIN_TRACK_CARD_ZOOM = 1;
export const MAX_TRACK_CARD_ZOOM = 2.5;
export const DEFAULT_TRACK_CARD_PLACEMENT = Object.freeze({focalX: 0.5, focalY: 0.5, zoom: 1});
export const DEFAULT_ARTWORK_PLACEMENT = Object.freeze({focalX: 0.5, focalY: 0.5});

function validCoordinate(value) {
  if (value == null || value === "" || typeof value === "boolean") return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= 1 ? number : null;
}

export function getTrackArtworkPlacement(track = {}, context = "default", enabled = storefrontMediaPresentationBuildEnabled()) {
  if (!enabled) return context === "track-card" ? {...DEFAULT_TRACK_CARD_PLACEMENT} : {...DEFAULT_ARTWORK_PLACEMENT};
  const placement = track && typeof track === "object" ? (track.artworkPlacement || {}) : {};
  const override = context === "track-card" ? (placement.trackCard || placement.overrides?.[context] || {}) :
    (placement.overrides?.[context] || {});
  const zoomValue = Number(override.zoom);
  return {
    focalX: validCoordinate(override.focalX) ?? validCoordinate(placement.focalX) ?? DEFAULT_ARTWORK_PLACEMENT.focalX,
    focalY: validCoordinate(override.focalY) ?? validCoordinate(placement.focalY) ?? DEFAULT_ARTWORK_PLACEMENT.focalY,
    ...(context === "track-card" ? {zoom: Number.isFinite(zoomValue) && zoomValue >= MIN_TRACK_CARD_ZOOM && zoomValue <= MAX_TRACK_CARD_ZOOM ? zoomValue : 1} : {}),
  };
}

export function artworkObjectPosition(track = {}, context = "default", enabled = storefrontMediaPresentationBuildEnabled()) {
  const {focalX, focalY} = getTrackArtworkPlacement(track, context, enabled);
  return `${focalX * 100}% ${focalY * 100}%`;
}

export function validateArtworkPlacement(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  if (validCoordinate(value.focalX) == null || validCoordinate(value.focalY) == null) return false;
  if (value.overrides != null) {
    if (!value.overrides || typeof value.overrides !== "object" || Array.isArray(value.overrides)) return false;
    if (Object.values(value.overrides).some((item) => !item || typeof item !== "object" ||
      validCoordinate(item.focalX) == null || validCoordinate(item.focalY) == null)) return false;
  }
  if (value.trackCard != null) {
    const card = value.trackCard;
    const zoom = Number(card?.zoom);
    if (!card || typeof card !== "object" || Array.isArray(card) ||
      validCoordinate(card.focalX) == null || validCoordinate(card.focalY) == null ||
      !Number.isFinite(zoom) || zoom < MIN_TRACK_CARD_ZOOM || zoom > MAX_TRACK_CARD_ZOOM) return false;
  }
  return true;
}

export function artworkImageStyle(track = {}, context = "default", enabled = storefrontMediaPresentationBuildEnabled()) {
  const placement = getTrackArtworkPlacement(track, context, enabled);
  return {
    objectPosition: `${placement.focalX * 100}% ${placement.focalY * 100}%`,
    ...(context === "track-card" && placement.zoom !== 1 ? {
      transform: `scale(${placement.zoom})`,
      transformOrigin: `${placement.focalX * 100}% ${placement.focalY * 100}%`,
    } : {}),
  };
}

export function focalPointFromContainedRect({clientX, clientY, left, top, width, height,
  naturalWidth, naturalHeight}) {
  if (![clientX, clientY, left, top, width, height, naturalWidth, naturalHeight]
    .every((value) => Number.isFinite(Number(value))) || width <= 0 || height <= 0 ||
    naturalWidth <= 0 || naturalHeight <= 0) return {...DEFAULT_ARTWORK_PLACEMENT};
  const scale = Math.min(width / naturalWidth, height / naturalHeight);
  const renderedWidth = naturalWidth * scale;
  const renderedHeight = naturalHeight * scale;
  const imageLeft = left + (width - renderedWidth) / 2;
  const imageTop = top + (height - renderedHeight) / 2;
  const clamp = (value) => Math.max(0, Math.min(1, value));
  return {
    focalX: clamp((clientX - imageLeft) / renderedWidth),
    focalY: clamp((clientY - imageTop) / renderedHeight),
  };
}

export function nudgeArtworkPlacement(value, key, shiftKey = false) {
  const current = getTrackArtworkPlacement({artworkPlacement: value});
  const step = shiftKey ? 0.1 : 0.01;
  const delta = {ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step],
    ArrowDown: [0, step]}[key];
  if (!delta) return current;
  const clamp = (number) => Math.max(0, Math.min(1, Number(number.toFixed(4))));
  return {focalX: clamp(current.focalX + delta[0]), focalY: clamp(current.focalY + delta[1])};
}

export function resolveTrackArtwork(track = {}, fallback = "") {
  const variants = track.artworkVariants || track.imageVariants || {};
  const url320 = variants.webp320?.url || variants[320]?.url || track.imageThumbnail320 || "";
  const url640 = variants.webp640?.url || variants[640]?.url || track.imageThumbnail640 || "";
  const original = canonicalPublicArtworkUrl(track.imageURL || fallback || "");
  return {
    src: canonicalPublicArtworkUrl(url640 || url320 || original || fallback),
    srcSet: [url320 && `${canonicalPublicArtworkUrl(url320)} 320w`,
      url640 && `${canonicalPublicArtworkUrl(url640)} 640w`].filter(Boolean).join(", ") || undefined,
    sizes: "(max-width: 480px) 86vw, (max-width: 900px) 44vw, 320px",
    width: Number(variants.width || track.imageWidth || 640),
    height: Number(variants.height || track.imageHeight || 640),
  };
}
